import { useEffect, useState } from 'react';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import {
  attributesConditionsMatch,
  canUserCustomAttributeBeEdited,
  canUserCustomAttributeBeViewed,
  getApplicableUserCustomAttributes
} from 'shared-helpers/customAttributes';
import {
  decodeCustomAttributeKeyForForm,
  encodeCustomAttributeKeyForForm
} from 'lib/customAttributes';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';
import { Form, FormGroup, Text } from 'components/common/form';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';
import { UserCustomAttribute } from 'components/user';

import type {
  UserCustomAttribute as IUserCustomAttribute,
  UserCustomAttributes
} from 'shared-types';

interface Props {
  modalVisible: boolean;
  setModalVisible: (modalVisible: boolean) => void;
}

const GuestInfoModal = ({ modalVisible, setModalVisible }: Props) => {
  const { dispatch } = useDispatch();
  const { messages, serverConfig, theme, user } = useReduxState();

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const newGuestId = sessionStorage.getItem('vuoNewGuestId');
    if (user?.guestId && newGuestId === user?.guestId) {
      sessionStorage.removeItem('vuoNewGuestId');
      setModalVisible(true);
    } else if (
      user?.roles.includes('guest') &&
      serverConfig.accountGuestUseName &&
      (!user.firstName || !user.lastName)
    ) {
      setModalVisible(true);
    }
  }, []);

  const handleClose = () => {
    setModalVisible(false);
  };

  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      const { customAttributesRequiredPasswords, ...otherValues } = values;

      const decodedCustomAttributes = Object.fromEntries(
        Object.entries(otherValues.customAttributes || {}).map(
          ([key, value]) => [decodeCustomAttributeKeyForForm(key), value]
        )
      );
      values.customAttributes = decodedCustomAttributes;

      const response = await api.post(`/auth/profile`, values);

      setIsSubmitting(false);

      if (response.data?.success) {
        dispatch.setUser({
          ...user,
          ...otherValues,
          customAttributes: {
            ...user.customAttributes,
            ...decodedCustomAttributes
          }
        });

        setModalVisible(false);
        dispatch.addNotice({
          text: messages.commonChangesSavesSuccessfully,
          type: 'success'
        });
      } else if (
        response.data?.error === 'invalid_custom_attribute_required_password'
      ) {
        await dispatch.handleErrorDispatch(
          null,
          messages.authRequiredPasswordInvalid
        );
        return false;
      } else {
        throw new Error('error');
      }
    } catch (err) {
      setIsSubmitting(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateRequired = (value) => {
    return !value || !value.toString().trim().length
      ? messages.commonFieldRequired
      : undefined;
  };

  const { accountGuestUseName, userCustomAttributes } = serverConfig;

  const requireName =
    user?.roles.includes('guest') &&
    accountGuestUseName &&
    (!user.firstName || !user.lastName);

  let applicableUserCustomAttributes: IUserCustomAttribute[] | null = null;
  if (requireName) {
    applicableUserCustomAttributes = getApplicableUserCustomAttributes(
      user,
      userCustomAttributes
    ).filter((attribute) =>
      canUserCustomAttributeBeViewed({ user, userCustomAttribute: attribute })
    );
  }

  return (
    <ModalPortal>
      <Form onSubmit={handleSubmit}>
        {({ formState }) => (
          <Modal
            closeButton={!requireName}
            onHide={requireName ? null : handleClose}
            style={{ maxWidth: 340 }}
            title={messages.usersGuestAccount}
            visible={modalVisible}
          >
            <ModalContent>
              <div className="content">
                <div className="icon">
                  <Icon
                    icon="user-circle"
                    style={{ fontSize: '3rem', marginTop: 15 }}
                  />
                </div>
                {requireName ? (
                  <p>{messages.readerGuestInfoLoginAlt}</p>
                ) : (
                  <p>{`${messages.readerGuestInfoLogin} ${user?.username}.`}</p>
                )}

                <p>{messages.readerGuestInfoSaving}</p>
                <p>
                  {messages.readerGuestInfoDeletion}{' '}
                  <em style={{ whiteSpace: 'nowrap' }}>
                    {messages.headerGuestLogoutDelete}
                  </em>
                  {'.'}
                </p>

                {requireName ? (
                  <div className="name">
                    <FormGroup
                      htmlFor="guest-first-name"
                      label={messages.usersFirstName}
                    >
                      <Text
                        error={formState.errors.firstName}
                        field="firstName"
                        id="guest-first-name"
                        validate={validateRequired}
                        validateOnChange={true}
                      />
                    </FormGroup>

                    <FormGroup
                      htmlFor="guest-last-name"
                      label={messages.usersLastName}
                    >
                      <Text
                        error={formState.errors.lastName}
                        field="lastName"
                        id="guest-last-name"
                        validate={validateRequired}
                        validateOnChange={true}
                      />
                    </FormGroup>
                  </div>
                ) : null}

                {applicableUserCustomAttributes?.length ? (
                  <div className="custom-attributes">
                    {applicableUserCustomAttributes.map((attribute, i) => {
                      const customAttributes: UserCustomAttributes = {
                        ...user.customAttributes,
                        ...(Object.fromEntries(
                          Object.entries(
                            formState.values?.customAttributes || {}
                          ).map(([key, value]) => [
                            decodeCustomAttributeKeyForForm(key),
                            value
                          ])
                        ) as UserCustomAttributes)
                      };

                      const conditionsMatch = attributesConditionsMatch({
                        conditions: attribute.conditions,
                        customAttributeValues: customAttributes,
                        user
                      });
                      if (!conditionsMatch) return null;

                      const isReadOnly = !canUserCustomAttributeBeEdited({
                        user,
                        userCustomAttribute: attribute
                      });

                      const isRequired = attribute.isRequired?.value
                        ? attributesConditionsMatch({
                            conditions: attribute.isRequired?.conditions || [],
                            customAttributeValues: customAttributes,
                            isRequiredCheck: true,
                            user
                          })
                        : false;

                      const error =
                        formState.errors[
                          encodeCustomAttributeKeyForForm(attribute.key)
                        ];

                      return (
                        <UserCustomAttribute
                          attribute={attribute}
                          calledFrom="guestInfo"
                          customAttributeOriginalValues={user.customAttributes}
                          customAttributeValues={{
                            ...user.customAttributes,
                            ...formState.values?.customAttributes
                          }}
                          error={error}
                          id={i}
                          isAttributeRequired={isRequired}
                          isReadOnly={isReadOnly}
                          key={`${attribute.key}-${i}-${isRequired}`}
                        />
                      );
                    })}
                  </div>
                ) : null}

                <style jsx>{`
                  .content {
                    color: ${theme.textColorLighter};
                    font-size: 1.5rem;
                    font-weight: 600;
                    padding: 5px;
                    text-align: center;
                  }
                  .icon {
                    display: inline-block;
                  }
                  .name {
                    text-align: left;
                  }
                  .custom-attributes {
                    margin-top: 15px;
                    text-align: left;
                  }
                `}</style>
              </div>
            </ModalContent>
            <ModalFooter>
              {requireName ? (
                <Button
                  category="success"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  text={messages.commonContinue}
                  type="submit"
                  style={{ width: '100%' }}
                />
              ) : (
                <Button
                  category="light"
                  onClick={handleClose}
                  text={messages.commonContinue}
                  style={{ width: '100%' }}
                />
              )}
            </ModalFooter>
          </Modal>
        )}
      </Form>
    </ModalPortal>
  );
};

export default GuestInfoModal;
